import { useState, useMemo } from "react";
import { Download, Search, FileSpreadsheet, Presentation, AppWindow } from "lucide-react";
import { toast } from "sonner";
import { RESOURCES, RESOURCE_CATEGORIES } from "../data/content";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { motion, AnimatePresence } from "framer-motion";

const TYPE_ICON = { "Excel": FileSpreadsheet, "PPTX": Presentation, "Web App": AppWindow };

export default function Ressources() {
  const [cat, setCat] = useState("Tout");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return RESOURCES.filter((r) => {
      const matchCat = cat === "Tout" || r.cat === cat;
      const matchQ = r.title.toLowerCase().includes(q.toLowerCase()) || r.desc.toLowerCase().includes(q.toLowerCase());
      return matchCat && matchQ;
    });
  }, [cat, q]);

  return (
    <>
      <PageHeader
        eyebrow="[ Outils & Ressources ]"
        titleLines={["Une boîte à outils", "prête à l'emploi."]}
        intro="Modèles Excel, présentations PowerPoint et applications web pour piloter votre performance industrielle. Téléchargeables gratuitement."
      />

      <section className="bg-paper py-14 sticky-filters" data-testid="resources-filters">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex flex-wrap gap-2">
              {RESOURCE_CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`filter-${c}`}
                  className={`label-mono px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                    cat === c ? "bg-ink text-paper border-ink" : "bg-transparent text-ink/70 hairline hover:border-ink"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-80">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="resources-search"
                placeholder="Rechercher un outil..."
                className="w-full bg-white border hairline rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-gold transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-paper pb-28" data-testid="resources-grid">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((r) => {
                const Icon = TYPE_ICON[r.type] || AppWindow;
                return (
                  <motion.article
                    key={r.title}
                    layout
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="group flex flex-col rounded-sm border hairline bg-white p-7 hover:border-gold transition-colors duration-400"
                    data-testid={`resource-${r.title}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-sm bg-ink-900 grain flex items-center justify-center">
                        <Icon size={20} className="text-gold relative z-10" />
                      </div>
                      <span className="label-mono text-ink/40">{r.type}</span>
                    </div>
                    <h3 className="font-display text-2xl font-semibold mt-6 leading-tight">{r.title}</h3>
                    <p className="text-ink/55 text-sm mt-3 flex-1 leading-relaxed">{r.desc}</p>
                    <button
                      onClick={() => toast.success(`« ${r.title} » — téléchargement lancé`, { description: "Merci de votre intérêt pour nos ressources." })}
                      data-testid={`download-${r.title}`}
                      className="group/btn inline-flex items-center justify-between mt-7 pt-5 border-t hairline label-mono text-ink hover:text-gold transition-colors"
                    >
                      Télécharger
                      <Download size={16} className="group-hover/btn:translate-y-0.5 transition-transform" />
                    </button>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>
          {filtered.length === 0 && (
            <Reveal><p className="text-center text-ink/50 py-20 font-display text-2xl">Aucun outil ne correspond à votre recherche.</p></Reveal>
          )}
        </div>
      </section>
    </>
  );
}
